import { useMemo, useState } from "react";
import {
  Card,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Grid,
  Icon,
  IconButton,
} from "@mui/material";
import MDBox from "components/MDBox";
import MDButton from "components/MDButton";
import MDInput from "components/MDInput";
import MDTypography from "components/MDTypography";
import useCreateDailyFormVehiclePart from "hooks/fleet/useCreateDailyFormVehiclePart";
import useDeleteDailyFormVehiclePart from "hooks/fleet/useDeleteDailyFormVehiclePart";
import useUpdateDailyFormVehiclePart from "hooks/fleet/useUpdateDailyFormVehiclePart";

interface VehiclePartsSectionProps {
  vehicleId: string;
  parts: any[];
  onSaved: () => Promise<any>;
}

const emptyPart = {
  id: null as string | null,
  name: "",
  vendorName: "",
  partNumber: "",
  unitPrice: "",
  quantity: "",
  notes: "",
};

export default function VehiclePartsSection({
  vehicleId,
  parts,
  onSaved,
}: VehiclePartsSectionProps) {
  const [createPart] = useCreateDailyFormVehiclePart();
  const [updatePart] = useUpdateDailyFormVehiclePart();
  const [deletePart] = useDeleteDailyFormVehiclePart();
  const [editingPart, setEditingPart] = useState<any | null>(null);

  const partRows = useMemo(() => parts || [], [parts]);

  const handleSave = async () => {
    const baseInput = {
      dailyFormVehicleId: vehicleId,
      name: editingPart.name,
      vendorName: editingPart.vendorName || null,
      partNumber: editingPart.partNumber || null,
      unitPrice: editingPart.unitPrice === "" ? null : Number(editingPart.unitPrice),
      quantity: editingPart.quantity === "" ? null : Number(editingPart.quantity),
      notes: editingPart.notes || null,
    };

    if (editingPart?.id) {
      await updatePart({ variables: { input: { ...baseInput, id: editingPart.id } } });
    } else {
      await createPart({ variables: { input: baseInput } });
    }

    setEditingPart(null);
    await onSaved();
  };

  const handleDelete = async (id: string) => {
    await deletePart({ variables: { id } });
    await onSaved();
  };

  return (
    <Card>
      <MDBox p={3}>
        <MDBox display="flex" justifyContent="space-between" alignItems="center" mb={2}>
          <MDTypography variant="h6" fontWeight="medium">
            Parts List
          </MDTypography>
          <MDButton color="blue" size="small" onClick={() => setEditingPart(emptyPart)}>
            <Icon>add</Icon>&nbsp;Add Part
          </MDButton>
        </MDBox>

        {partRows.length === 0 ? (
          <MDTypography variant="body2" color="text">
            No parts configured for this vehicle.
          </MDTypography>
        ) : (
          <Grid container spacing={2}>
            {partRows.map((part) => (
              <Grid item xs={12} key={part.id}>
                <MDBox
                  px={2}
                  py={1.5}
                  border="1px solid #e0e0e0"
                  borderRadius="8px"
                  display="flex"
                  justifyContent="space-between"
                  alignItems="flex-start"
                >
                  <div>
                    <MDTypography variant="button" fontWeight="medium">
                      {part.name}
                    </MDTypography>
                    <MDTypography variant="caption" display="block" color="text">
                      Vendor: {part.vendorName || "N/A"} | Part #: {part.partNumber || "N/A"}
                    </MDTypography>
                    <MDTypography variant="caption" display="block" color="text">
                      Price: {part.unitPrice != null ? `$${part.unitPrice}` : "N/A"} | Qty: {part.quantity ?? "N/A"}
                    </MDTypography>
                  </div>
                  <MDBox display="flex" gap={1}>
                    <IconButton size="small" onClick={() => setEditingPart({ ...part })}>
                      <Icon>edit</Icon>
                    </IconButton>
                    <IconButton size="small" color="error" onClick={() => handleDelete(part.id)}>
                      <Icon>delete</Icon>
                    </IconButton>
                  </MDBox>
                </MDBox>
              </Grid>
            ))}
          </Grid>
        )}
      </MDBox>

      <Dialog open={!!editingPart} onClose={() => setEditingPart(null)} fullWidth maxWidth="sm">
        <DialogTitle>{editingPart?.id ? "Edit Part" : "Add Part"}</DialogTitle>
        <DialogContent>
          <MDBox mt={1} display="grid" gap={2}>
            <MDInput
              label="Part Name"
              value={editingPart?.name || ""}
              onChange={(e: any) => setEditingPart((prev: any) => ({ ...prev, name: e.target.value }))}
            />
            <MDInput
              label="Vendor"
              value={editingPart?.vendorName || ""}
              onChange={(e: any) => setEditingPart((prev: any) => ({ ...prev, vendorName: e.target.value }))}
            />
            <MDInput
              label="Part Number"
              value={editingPart?.partNumber || ""}
              onChange={(e: any) => setEditingPart((prev: any) => ({ ...prev, partNumber: e.target.value }))}
            />
            <MDInput
              label="Unit Price"
              type="number"
              value={editingPart?.unitPrice ?? ""}
              onChange={(e: any) => setEditingPart((prev: any) => ({ ...prev, unitPrice: e.target.value }))}
            />
            <MDInput
              label="Quantity"
              type="number"
              value={editingPart?.quantity ?? ""}
              onChange={(e: any) => setEditingPart((prev: any) => ({ ...prev, quantity: e.target.value }))}
            />
            <MDInput
              label="Notes"
              multiline
              rows={3}
              value={editingPart?.notes || ""}
              onChange={(e: any) => setEditingPart((prev: any) => ({ ...prev, notes: e.target.value }))}
            />
          </MDBox>
        </DialogContent>
        <DialogActions>
          <MDButton color="secondary" onClick={() => setEditingPart(null)}>
            Cancel
          </MDButton>
          <MDButton color="info" onClick={handleSave} disabled={!editingPart?.name}>
            Save
          </MDButton>
        </DialogActions>
      </Dialog>
    </Card>
  );
}
